import { FormFieldLabel, gradients } from '@alison/ui';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { usePlatformFields } from '../../lib/workflows.hooks.js';
import { CampaignField } from './CampaignField.js';

const PANEL_SX = {
  flex: 1,
  minWidth: 420,
  gap: 2.5,
  p: 4,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const LABEL_SX = { color: 'text.disabled', fontSize: 10 };
const HINT_SX = { fontSize: 10, color: 'text.muted' };

const EDIT_SX = { color: 'primary.main', borderColor: 'primary.main' };

const RESCORE_SX = {
  background: gradients.primary,
  '&:hover': { background: gradients.primaryHover },
  '&.Mui-disabled': { background: gradients.primary, opacity: 0.45, color: 'common.white' },
};

const MODE_LABEL: Record<string, string> = {
  both: 'Best practice & Performance',
  performance: 'Historical performance',
  best_practice: 'Best practice',
};

export interface AdjustConfigurationProps {
  platform: string;
  scoringMode: string | null;
  kpi: string | null;
  entered: Record<string, string>;
  busy?: boolean;
  error?: string;
  onRescore: (selected: Record<string, string>) => void;
}

function Detail({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <Stack sx={{ gap: 0.5, minWidth: 180 }}>
      <Typography sx={{ fontSize: 11, color: 'text.muted' }}>{label}</Typography>
      <Typography sx={{ fontSize: 11 }}>{value}</Typography>
    </Stack>
  );
}

export function AdjustConfiguration({
  platform,
  scoringMode,
  kpi,
  entered,
  busy = false,
  error,
  onRescore,
}: AdjustConfigurationProps): ReactNode {
  const [edited, setEdited] = useState<Record<string, string> | null>(null);
  const editing = edited !== null;
  const selected = edited ?? entered;
  const { fields, isFetching } = usePlatformFields(platform, selected);
  const pickable = fields.filter((field) => field.kind === 'select');

  return (
    <Stack sx={PANEL_SX}>
      <Stack sx={{ gap: 0.5 }}>
        <Typography variant="h3" sx={{ fontSize: 15 }}>
          Adjust configuration
        </Typography>
        <Typography sx={{ color: 'text.muted' }}>
          Change the inputs and re-score with the new settings.
        </Typography>
      </Stack>

      <Stack sx={{ gap: 1.5 }}>
        <FormFieldLabel sx={LABEL_SX}>BATCH DETAILS</FormFieldLabel>
        <Stack direction="row" sx={{ gap: 6, flexWrap: 'wrap' }}>
          <Detail
            label="Score based on"
            value={scoringMode === null ? '—' : (MODE_LABEL[scoringMode] ?? scoringMode)}
          />
          <Detail label="Primary KPI" value={kpi === null ? '—' : kpi.toUpperCase()} />
        </Stack>
      </Stack>

      <Divider />

      <Stack sx={{ gap: 1.5 }}>
        <Stack direction="row" sx={{ gap: 1 }}>
          <FormFieldLabel sx={LABEL_SX}>CAMPAIGN DETAILS</FormFieldLabel>
          <Typography sx={HINT_SX}>· select to re-score</Typography>
        </Stack>
        <Box
          sx={{
            display: 'grid',
            gap: 2.5,
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          }}
        >
          {pickable.map((field) => (
            <CampaignField
              key={field.key}
              field={field}
              value={selected[field.key] ?? ''}
              busy={busy || isFetching}
              readOnly={!editing}
              onSet={(next) => setEdited({ ...selected, [field.key]: next })}
            />
          ))}
        </Box>
      </Stack>

      {error === undefined ? null : (
        <Alert severity="error" sx={{ fontSize: 13 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" sx={{ gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          disabled={busy}
          sx={EDIT_SX}
          onClick={() => setEdited(editing ? null : entered)}
        >
          {editing ? 'Cancel' : 'Edit'}
        </Button>
        <Button
          variant="contained"
          disabled={!editing || busy}
          onClick={() => onRescore(selected)}
          sx={RESCORE_SX}
        >
          {busy ? 'Re-scoring…' : 'Re-score creative'}
        </Button>
      </Stack>
    </Stack>
  );
}
